import * as React from 'react'
import { MarketplaceHeaderCTA } from '../marketplace-header-cta'
import * as NavigationMenu from 'primitives/navigation-menu'
import { tw } from 'utils/classnames'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { useIsStripeBillingEnabled } from '@repo/site-data/hooks/use-site-data'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { ClientOnly } from 'components/client-only'

import * as MenuItem from '../menu-item'
import { UserMenu } from './user-menu'
import { LanguageSwitcher } from './language-and-currency'
import {
  useHeaderContext,
  useLinkComponent,
  useOnLoginClick,
  useOnSignUpClick,
  useTranslations,
} from '../../contexts/header-config-context'
import { MainNavigation } from './main-navigation'
import { StoreMenu } from './store-menu'
import { HeartRoundedIcon } from '../icons/heart-rounded-icon'
import { SparkInpaintingIcon } from '../icons/spark-inpainting-icon'
import { studioBaseUrl } from '../../utils/urls'
import { useIsIpad } from '../../hooks/use-is-ipad'
import { SearchInput } from '../search/search-input'
import { SearchAutocomplete } from '../search'
import {
  IconArrowForward,
  IconFeaturedSeasonalAndGifts,
} from '@repo/design-system-cf/components/icons'
import {
  PAYMENT_ENTRY_POINTS,
  savePaymentModalEntryPoint,
} from 'payments/payments-dialog/analytics'
import { useIsAuthPending, useIsLoggedIn } from '../../hooks/use-auth'
import { Coin } from '@repo/studio-design-system-ui-coin'

import { useUserCoinBalanceQuery } from 'codegen/generated/coins'
import { useAnimatedNumber, formatLocaleInteger } from 'hooks/use-animated-number'
export const containerClasses = 'm-auto w-full px-4 xl:max-w-7xl 2xl:max-w-[1536px]'

const LazyOnlineCount = React.lazy(() =>
  import('./online-count').then(m => ({ default: m.OnlineCount })),
)

function OnlineCountFallback({ className }: { className?: string }) {
  return (
    <div
      className={tw(
        'flex items-center rounded-[8px] py-2 pr-3 text-12 text-cf-dreamy-cloud-900',
        className,
      )}
    >
      <div className="flex h-5 w-5 items-center justify-center">
        <div className="bg-clover-500 relative flex h-2.5 w-2.5 rounded-full">
          <div className="bg-clover-500 absolute flex h-2.5 w-2.5 animate-ping rounded-full" />
        </div>
      </div>
      <span className="grow whitespace-nowrap">1,784</span>
    </div>
  )
}

function OnlineCount({ className }: { className?: string }) {
  return (
    <React.Suspense fallback={<OnlineCountFallback className={className} />}>
      <LazyOnlineCount className={className} />
    </React.Suspense>
  )
}

function CoinBalance({ className }: { className?: string }) {
  const { data } = useUserCoinBalanceQuery(undefined, {
    select: d => d.userBalance.balance,
  })

  const balance = data ?? 0
  const displayValue = useAnimatedNumber({ value: balance, format: formatLocaleInteger })
  const targetValue = Math.round(balance).toLocaleString()

  return (
    <MenuItem.Link
      href={AppRoutes.COINS}
      data-testid="coin-balance"
      className={tw('gap-1.5', className)}
    >
      <Coin className="h-5 w-5 [--theme_component_asset_coin_background-color:theme(colors.yellow.500)] [--theme_component_asset_coin_border-color:theme(colors.orange.500)] [--theme_component_asset_coin_foreground-color:theme(colors.orange.500)]" />
      <span className="relative inline-flex whitespace-nowrap">
        <span className="invisible">{targetValue}</span>
        <span className="absolute inset-0 text-right">{displayValue}</span>
      </span>
    </MenuItem.Link>
  )
}

export function DesktopHeader({ mainNavigation }: { mainNavigation?: React.ReactNode } = {}) {
  const { lang, isHomepage } = useHeaderContext()
  const Link = useLinkComponent()
  const isAuthLoading = useIsAuthPending()
  const isAuthenticated = useIsLoggedIn()
  const menuItemStyle = isAuthenticated ? 'flex-col gap-0.5' : ''
  const t = useTranslations()
  const onLoginClick = useOnLoginClick()
  const onSignUpClick = useOnSignUpClick()
  const isIpad = useIsIpad()
  const isStripeBillingEnabled = useIsStripeBillingEnabled()
  const showFreeTrialCTA = !isAuthenticated
  const logoHref = isHomepage ? '/' : '/content/'

  const loadingUI = (
    <div className="w-[386px]">
      <LanguageSwitcher classNames="cf-hidden" forceMount />
    </div>
  )

  const unauthenticatedUI = (
    <>
      <LanguageSwitcher />
      {isHomepage && (
        <>
          <NavigationMenu.Item key="online-count">
            <OnlineCount />
          </NavigationMenu.Item>
          <NavigationMenu.Item key="earn-coins">
            <MenuItem.Link
              href={`${studioBaseUrl}/referrals`}
              target="_blank"
              rel="noopener noreferrer"
              className="gap-1.5"
            >
              <IconFeaturedSeasonalAndGifts className="h-5" />
              <span className="grow whitespace-nowrap">{t('top_nav.earn_free_coins')}</span>
            </MenuItem.Link>
          </NavigationMenu.Item>
        </>
      )}
      <NavigationMenu.Item key="login">
        <MenuItem.Button
          onClick={onLoginClick}
          data-testid="login-button"
          className={tw(menuItemStyle, 'grow whitespace-nowrap')}
        >
          {t('top_nav.login')}
        </MenuItem.Button>
      </NavigationMenu.Item>
      <NavigationMenu.Item key="signUp">
        <MenuItem.Button
          data-testid="sign-up-button"
          variant="outlined"
          className={tw('ml-1', menuItemStyle, 'grow whitespace-nowrap')}
          onClick={onSignUpClick}
        >
          {t('top_nav.sign_up')}
        </MenuItem.Button>
      </NavigationMenu.Item>
    </>
  )
  const authenticatedUI = (
    <>
      <LanguageSwitcher />
      {isHomepage ? (
        <>
          <NavigationMenu.Item key="online-count">
            <OnlineCount className={menuItemStyle} />
          </NavigationMenu.Item>
          <NavigationMenu.Item key="coin-balance">
            <CoinBalance className={menuItemStyle} />
          </NavigationMenu.Item>
          <NavigationMenu.Item key="earn-coins">
            <MenuItem.Link
              href={`${studioBaseUrl}/referrals`}
              target="_blank"
              rel="noopener noreferrer"
              className={menuItemStyle}
            >
              <IconFeaturedSeasonalAndGifts className="h-5" />
              <span className="grow whitespace-nowrap">{t('top_nav.earn_free_coins')}</span>
            </MenuItem.Link>
          </NavigationMenu.Item>
        </>
      ) : (
        <>
          <NavigationMenu.Item key="coin-balance">
            <CoinBalance className={menuItemStyle} />
          </NavigationMenu.Item>
          <NavigationMenu.Item key="favorites">
            <NavigationMenu.Trigger asChild data-testid="favorites-button">
              <MenuItem.Link href="/my-favorites/" className={menuItemStyle}>
                <HeartRoundedIcon className="h-5" />
                <span className="grow whitespace-nowrap"> {t('top_nav.favorites')}</span>
              </MenuItem.Link>
            </NavigationMenu.Trigger>
          </NavigationMenu.Item>
        </>
      )}
      <StoreMenu />
      <UserMenu />
    </>
  )

  const HeaderCTA = () => (
    <>
      {isAuthLoading ? (
        <span />
      ) : showFreeTrialCTA ? (
        <MenuItem.Link
          className="ml-4 min-w-24 py-1.5 px-3"
          variant="colored"
          data-testid="header-cta"
          href={
            isHomepage
              ? AppRoutes.B_FREE_TRIAL_AISTUDIO
              : isStripeBillingEnabled
                ? AppRoutes.B_FREE_TRIAL
                : AppRoutes.FREE_TRIAL
          }
          onClick={() => {
            savePaymentModalEntryPoint(PAYMENT_ENTRY_POINTS.MAIN_MENU_STUDIO_BUTTON)
            sendAmplitudeEvent(AmplitudeEvents.MAIN_MENU_STUDIO_BUTTON_CLICKED)
          }}
          target="_self"
        >
          <span className="drop-shadow-cf-elevation-light-1 text-14 grow text-center inline-flex items-center justify-center gap-1 whitespace-nowrap">
            <span className="whitespace-nowrap">{t('top_nav.free_trial')}</span>
            <span>🎉</span>
            <IconArrowForward className="h-4 w-4" />
          </span>
        </MenuItem.Link>
      ) : (
        <MenuItem.Link
          className="ml-4 min-w-24 py-1.5"
          variant="colored"
          data-testid="header-cta"
          href={studioBaseUrl}
          onClick={() => {
            savePaymentModalEntryPoint(PAYMENT_ENTRY_POINTS.MAIN_MENU_STUDIO_BUTTON)
            sendAmplitudeEvent(AmplitudeEvents.MAIN_MENU_STUDIO_BUTTON_CLICKED)
          }}
          target="_blank"
        >
          <SparkInpaintingIcon className="text-cf-white drop-shadow-cf-elevation-light-1 h-5" />
          <span className="drop-shadow-cf-elevation-light-1 text-14">{t('top_nav.create')}</span>
        </MenuItem.Link>
      )}
    </>
  )

  return (
    <>
      <div
        id="desktop-header"
        className={tw(
          'border-cf-dreamy-cloud-500 border-b ',
          isIpad ? 'cf-hidden' : 'cf-hidden min-[1175px]:block',
        )}
      >
        <div className={tw(containerClasses, 'flex h-14 items-center gap-6 px-6')}>
          <Link
            href={logoHref}
            className="text-cf-dark-aubergine text-20 xl:text-24 whitespace-nowrap font-bold"
            data-testid="logo"
          >
            Creative Fabrica
          </Link>
          <div className="w-full">
            <ClientOnly fallback={<SearchInput value="" onChange={val => val} />}>
              <SearchAutocomplete />
            </ClientOnly>
          </div>
          <NavigationMenu.Root lang={lang} aria-label="Top navigation">
            <NavigationMenu.List className="text-cf-dreamy-cloud-900 flex items-center">
              <li className="border-cf-dreamy-cloud-400 h-9 border-l" />
              {isAuthLoading ? loadingUI : isAuthenticated ? authenticatedUI : unauthenticatedUI}
              <NavigationMenu.Item key="create">
                {isHomepage ? <HeaderCTA /> : <MarketplaceHeaderCTA layout="desktop" />}
              </NavigationMenu.Item>
            </NavigationMenu.List>
          </NavigationMenu.Root>
        </div>
      </div>
      {mainNavigation ?? <MainNavigation />}
    </>
  )
}
